import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Slider from "react-slick";
import { useWallet } from "@aptos-labs/wallet-adapter-react";
import { BkfcDrawer } from "../bkfc/components/BkfcDrawer";
import { BkfcConnectDrawer } from "../bkfc/components/BkfcConnectDrawer";
import { BkfcStepRow } from "../bkfc/components/BkfcStepRow";
import { BkfcBackButton } from "../bkfc/components/BkfcBackButton";
import { useBkfcClaim } from "../bkfc/useBkfcClaim";
import { fetchClaimStatus, sendRaffleLink, BkfcRequestError } from "../bkfc/api";
import { getRecaptchaToken } from "../bkfc/recaptcha";
import { BKFC_NETWORK_LABEL } from "../bkfc/config";

const PRODUCT_DETAILS = [
  { label: "Name:", value: "BKFC BOOM FISTS" },
  { label: "Brand:", value: "BKFC" },
  { label: "Size:", value: "22 CM/ 9 INCHES" },
  { label: "Material:", value: "Polyurethane Foam" },
  { label: "Release Date:", value: "2026" },
];

// "How it works" carousel (Figma 3608:78 and the two home variants).
const CAROUSEL_CARDS = [
  {
    id: 1,
    image: "/slider/bkfc-claim/1.png",
    title: "Tap the chip",
    description:
      "Hold your phone near the BKFC tag. A page will open, click <span class='font-medium text-black'>Claim now or Sign up</span>.",
  },
  {
    id: 2,
    image: "/slider/bkfc-claim/2.png",
    title: "Connect & confirm",
    description:
      "Choose your wallet app and connect it. When asked, approve the connection. You’ll see “Connected successfully.” Close the tab and tap the tag again.",
  },
  {
    id: 3,
    image: "/slider/bkfc-claim/3.png",
    title: "Claim your Certificate of Authenticity",
    description:
      "The Certificate of Authenticity preview will appear. Tap Claim and confirm once more in your wallet. You’ll see a success message. Your BKFC Certificate is now yours!",
  },
];

// Full-page phase of the flow (each mirrors a Figma screen).
type View =
  | "home"
  | "confirm"
  | "approve"
  | "minting"
  | "claimed"
  | "check_email";

type DrawerType =
  | "checking"
  | "connect"
  | "email"
  | "already_claimed"
  | "error"
  | null;

const shortAddress = (addr: string | null | undefined) =>
  addr ? `${addr.slice(0, 6)}...${addr.slice(-4)}` : "";

export const ClaimBkfc = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawUid = searchParams.get("uid");
  const uid =
    rawUid && /^[A-F0-9]+$/i.test(rawUid) ? rawUid.toUpperCase() : null;

  const { connected, account, wallet } = useWallet();
  const { claim, status, result, error, reset } = useBkfcClaim(uid);

  const [view, setView] = useState<View>("home");
  const [drawer, setDrawer] = useState<DrawerType>(null);
  // remembered past drawer dismissal so Claim Now can't start a doomed flow
  const [alreadyClaimed, setAlreadyClaimed] = useState(false);
  const [serial, setSerial] = useState<number | null>(null);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [recaptchaState, setRecaptchaState] = useState<
    "idle" | "verifying" | "verified"
  >("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [raffleSending, setRaffleSending] = useState(false);
  const [raffleUrl, setRaffleUrl] = useState<string | null>(null);
  const [raffleNotice, setRaffleNotice] = useState<string | null>(null);
  const [approveRetryCooldown, setApproveRetryCooldown] = useState(false);
  // where "Please Try Again" should land: home before the email step, confirm after
  const errorReturnRef = useRef<View>("home");

  const sliderRef = useRef<Slider>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderSettings = {
    dots: false,
    infinite: false,
    speed: 450,
    slidesToShow: 1,
    slidesToScroll: 1,
    swipeToSlide: true,
    arrows: false,
    afterChange: (current: number) => setCurrentSlide(current),
  };

  // The flow swaps views in place on one route, so the previous screen's
  // scroll position carries over and the next screen opens cut off at the
  // top (under the sticky header). Reset scroll on every view change.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [view]);

  // ---- initial on-chain claim-status check (Figma "Checking this tag") ----
  useEffect(() => {
    let isMounted = true;

    const checkUid = async () => {
      if (!rawUid) return;

      if (!uid) {
        setErrorMessage("Invalid UID format");
        errorReturnRef.current = "home";
        setDrawer("error");
        return;
      }

      setDrawer("checking");
      try {
        const statusRes = await fetchClaimStatus(uid);
        if (!isMounted) return;
        setSerial(statusRes.serial ?? null);
        if (statusRes.action === "already_claimed") {
          setAlreadyClaimed(true);
          setDrawer("already_claimed");
        } else {
          setAlreadyClaimed(false);
          setDrawer(null);
        }
      } catch (err) {
        if (!isMounted) return;
        if (err instanceof BkfcRequestError && err.status === 400) {
          // unknown / malformed UID reads as "already claimed or invalid"
          setAlreadyClaimed(true);
          setDrawer("already_claimed");
        } else {
          setErrorMessage(
            err instanceof BkfcRequestError && err.status === 0
              ? "Could not reach the claim service. Check your connection and try again."
              : err instanceof Error
                ? err.message
                : "Something went wrong",
          );
          errorReturnRef.current = "home";
          setDrawer("error");
        }
      }
    };

    checkUid();
    return () => {
      isMounted = false;
    };
  }, [rawUid, uid]);

  // ---- map claim-hook status onto the Figma screens ----
  useEffect(() => {
    switch (status) {
      case "requesting-signature":
      case "awaiting-approval":
        setView("approve");
        break;
      case "waiting-confirmation":
      case "verifying":
        setView("minting");
        break;
      case "confirmed":
        setView("claimed");
        break;
      case "error":
        if (error?.kind === "already-claimed") {
          setAlreadyClaimed(true);
          setView("home");
          setDrawer("already_claimed");
        } else {
          setErrorMessage(error ? error.description : "Something went wrong");
          errorReturnRef.current = "confirm";
          setView("confirm");
          setDrawer("error");
        }
        break;
      default:
        break;
    }
  }, [status, error]);

  const handleClaimNow = () => {
    // dismissing the already-claimed drawer must not open a path to a claim
    // that can only end in the same error
    if (alreadyClaimed) {
      setDrawer("already_claimed");
      return;
    }
    if (connected) {
      setDrawer("email");
    } else {
      setDrawer("connect");
    }
  };

  // Without a uid there is nothing to claim yet — just close the drawer and
  // show the connected home. Testers/buyers arrive with ?uid= links.
  const handleConnected = useCallback(() => {
    if (alreadyClaimed) {
      setDrawer("already_claimed");
      return;
    }
    setDrawer(uid ? "email" : null);
  }, [uid, alreadyClaimed]);

  const handleEmailSubmit = () => {
    if (!email) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }
    setEmailError(null);
    setDrawer(null);
    setRecaptchaState("idle");
    setView("confirm");
  };

  const handleRecaptchaCheck = async () => {
    if (recaptchaState !== "idle") return;
    setRecaptchaState("verifying");
    try {
      await getRecaptchaToken();
      setRecaptchaState("verified");
    } catch (err) {
      console.error("BKFC recaptcha error:", err);
      setRecaptchaState("idle");
      setErrorMessage(
        "Could not verify you're not a robot. Please try again.",
      );
      errorReturnRef.current = "confirm";
      setDrawer("error");
    }
  };

  const handleClaimCertificate = () => {
    if (recaptchaState !== "verified" || !account) return;
    claim(email);
  };

  // Re-opens the wallet when the approval prompt was lost (e.g. a silently
  // blocked popup) by re-running the claim with a fresh signature. The
  // cooldown avoids double-submission storms; a duplicate attempt is caught
  // by the gas station's simulation before it reaches the chain.
  const handleApproveRetry = () => {
    if (status !== "awaiting-approval" || approveRetryCooldown) return;
    setApproveRetryCooldown(true);
    window.setTimeout(() => setApproveRetryCooldown(false), 5000);
    claim(email);
  };

  const handleRunRaffle = async () => {
    if (!uid || raffleSending) return;
    setRaffleSending(true);
    setRaffleNotice(null);
    try {
      const res = await sendRaffleLink(uid);
      if (res.raffleUrl) setRaffleUrl(res.raffleUrl);
      if (!res.success && res.cooldownSeconds) {
        setRaffleNotice(
          `A link was already sent — you can resend in ${res.cooldownSeconds}s`,
        );
      }
      setView("check_email");
    } catch (err) {
      setErrorMessage(
        err instanceof Error ? err.message : "Failed to send the raffle link",
      );
      errorReturnRef.current = "claimed";
      setDrawer("error");
    } finally {
      setRaffleSending(false);
    }
  };

  const handleErrorRetry = () => {
    setErrorMessage(null);
    setDrawer(null);
    reset();
    setView(errorReturnRef.current);
  };

  // ---- back navigation between full-page phases ----
  const handleBackToHome = () => {
    reset();
    setView("home");
  };

  // reset() bumps the claim run id, so the in-flight attempt's late state
  // updates are dropped instead of yanking the view forward again. If the
  // wallet approval still goes through afterwards, a re-claim is answered
  // with the already-claimed error, which is accurate.
  const handleBackToConfirm = () => {
    reset();
    setView("confirm");
  };

  const walletLabel = wallet?.name ?? "wallet";
  const accountAddress = account?.address?.toString() ?? "";

  // ------------------------------------------------------------------ home
  const renderHome = () => (
    <div className="px-6 pt-8 pb-6">
      <h1 className="text-2xl font-bold text-center">BKFC 92 BOSTON</h1>

      {/* Certificate card */}
      <div className="relative mt-6 w-full aspect-[377/379]">
        <img
          src="/images/bkfc/cert-coin.png"
          alt="BKFC Certificate of Authenticity"
          className="absolute inset-0 w-full h-full object-cover rounded-[22px]"
        />

        {uid ? (
          <div className="absolute left-3 right-3 bottom-3 h-[55px] rounded-[19px] border-[0.5px] border-white bg-white/25 backdrop-blur-[5px] flex items-center justify-center">
            <button
              onClick={handleClaimNow}
              className="bg-[#343434] h-10 px-10 rounded-[50px] text-white font-bold hover:bg-[#4a4a4a] transition-colors"
            >
              Claim Now
            </button>
          </div>
        ) : (
          <div className="absolute left-3 right-3 bottom-3 rounded-[19px] border-[0.5px] border-white bg-white/25 backdrop-blur-[5px] p-4">
            {/* Figma update (node 3741:64): the "Tap NFC tag and open link"
                button was removed and the connect button relabeled "Sign in
                to claim" — the tap-tag overlay is now reached only after
                connecting. While connected the drawer offers
                Continue / Disconnect. */}
            <button
              onClick={() => setDrawer("connect")}
              className="bg-[#FFD43E] h-10 w-full rounded-[50px] text-black font-bold transition-colors hover:bg-[#f5c822]"
            >
              {connected && accountAddress
                ? `Connected · ${shortAddress(accountAddress)}`
                : "Sign in to claim"}
            </button>
          </div>
        )}
      </div>

      {/* How it works carousel */}
      <div className="mt-6">
        <h2 className="text-[23px] font-bold text-center">How it works</h2>

        <div className="relative mt-4 carousel-container">
          <Slider ref={sliderRef} {...sliderSettings}>
            {CAROUSEL_CARDS.map((card) => (
              <div key={card.id} className="w-full">
                <div className="relative w-full aspect-[380/370]">
                  <img
                    src={card.image}
                    alt={card.title}
                    className="object-contain absolute inset-0 w-full h-full"
                  />
                </div>
              </div>
            ))}
          </Slider>
        </div>

        <div className="mt-4 text-center">
          <h3 className="text-[22px] font-bold">
            {CAROUSEL_CARDS[currentSlide].title}
          </h3>
          <p
            className="mt-3 text-lg leading-tight text-[#626262]"
            dangerouslySetInnerHTML={{
              __html: CAROUSEL_CARDS[currentSlide].description,
            }}
          ></p>
        </div>

        <div className="flex gap-1 justify-center items-center mt-4">
          {CAROUSEL_CARDS.map((_, index) => (
            <div
              key={index}
              className={`rounded-full cursor-pointer transition-all ${
                currentSlide === index
                  ? "w-[37px] h-[10px] bg-black"
                  : "w-[10px] h-[10px] bg-[#CCCCCC]"
              }`}
              onClick={() => sliderRef.current?.slickGoTo(index)}
            />
          ))}
        </div>
      </div>

      {serial !== null && (
        <p className="mt-6 text-lg text-[#626262] text-center">
          Serial: #223-BKFC-{serial}
        </p>
      )}

      <div className="mt-6">
        <h2 className="text-2xl font-bold text-center">Product Details</h2>

        <div className="mt-4 space-y-1.5">
          {PRODUCT_DETAILS.map(({ label, value }) => (
            <div
              key={label}
              className="flex items-center justify-between text-sm"
            >
              <span className="font-medium uppercase">{label}</span>
              <span>{value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Figma node 3608:90 is 380x214 — exactly the poster's own 16:9, so
          the design shows it uncropped. A fixed height crops the sides on
          narrower columns; keep the intrinsic ratio instead. */}
      <img
        src="/images/bkfc/event-poster.jpg"
        alt="BKFC 92 Boston event poster"
        className="mt-6 w-full h-auto rounded-[22px]"
      />
    </div>
  );

  // --------------------------------------------------------------- confirm
  const renderConfirm = () => (
    <div className="px-6 pt-8 pb-6">
      <BkfcBackButton onClick={handleBackToHome} className="mb-4" />

      <h1 className="text-2xl font-bold text-center">Confirm and claim</h1>

      <img
        src="/images/bkfc/cert-card.png"
        alt="BKFC Certificate of Authenticity"
        className="mt-6 w-full h-[264px] object-cover rounded-[22px]"
      />

      <p className="mt-3 text-sm text-[#626262] text-center break-all">{uid}</p>

      <p className="mt-3 text-base text-[#626262] text-center">
        This certificate will be sent to{" "}
        <span className="underline">{email}</span> and minted to your connected
        wallet ({shortAddress(accountAddress)})
      </p>

      {/* recaptcha checkbox (token generation happens on tap) */}
      <button
        onClick={handleRecaptchaCheck}
        className="mt-6 w-full h-[58px] rounded-[18px] bg-[#FAFAF8] border border-[#B8B8B6] flex items-center gap-3 pl-2.5 pr-12"
      >
        <span className="w-10 h-10 rounded-[10px] bg-[#FAFAF8] border-2 border-[#B8B8B6] flex items-center justify-center shrink-0">
          {recaptchaState === "verified" && (
            <svg
              viewBox="0 0 16 16"
              className="w-5 h-5"
              fill="none"
              stroke="black"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M2.5 8.5l3.5 3.5 7.5-8" />
            </svg>
          )}
          {recaptchaState === "verifying" && (
            <span className="w-4 h-4 border-2 border-[#B8B8B6] border-t-black rounded-full animate-spin" />
          )}
        </span>
        <span className="text-base font-medium text-black">
          {recaptchaState === "verified" ? "Verified" : "I’m not a robot"}
        </span>
      </button>

      <button
        onClick={handleClaimCertificate}
        disabled={recaptchaState !== "verified"}
        className="mt-6 w-full h-[46px] rounded-[80px] font-bold text-base transition-colors bg-[#FFD43E] text-black hover:bg-[#f5c822] disabled:bg-[#B4B4B4] disabled:text-white disabled:cursor-not-allowed"
      >
        {recaptchaState === "verified" ? "Claim certificate" : "Verify to continue"}
      </button>

      <p className="mt-3 text-base text-[#626262] text-center">
        Gas sponsored - no APT needed
      </p>
    </div>
  );

  // --------------------------------------------------------------- approve
  const renderApprove = () => (
    <div className="px-6 pt-8 pb-6">
      <BkfcBackButton onClick={handleBackToConfirm} />

      <div className="min-h-[55vh] flex flex-col justify-center">
      <h1 className="text-2xl font-bold text-center">
        Please confirm your Certificate
      </h1>

      <p className="mt-6 text-base text-[#626262] text-center">
        Click the button below to proceed to the approval page and receive
        your Certificate of Authenticity.
      </p>

      <div className="mt-6 flex flex-col gap-3 items-start">
        <BkfcStepRow
          state={status === "requesting-signature" ? "active" : "done"}
          label="Signature issued by backend"
        />
        <BkfcStepRow
          state={status === "awaiting-approval" ? "active" : "pending"}
          label="Waiting for your approval"
        />
        <BkfcStepRow state="pending" label="Transaction submitted" />
      </div>

      <button
        disabled={status === "requesting-signature" || approveRetryCooldown}
        onClick={handleApproveRetry}
        className="mt-6 w-full h-[46px] rounded-[80px] bg-black text-white font-bold text-base disabled:bg-[#B4B4B4]"
      >
        Approve
      </button>
      </div>
    </div>
  );

  // --------------------------------------------------------------- minting
  const renderMinting = () => (
    <div className="px-6 pt-8 pb-6 min-h-[60vh] flex flex-col items-center justify-center">
      <img
        src="/images/bkfc/loading-badge.png"
        alt="Loading"
        className="w-20 animate-pulse"
      />

      <h1 className="mt-6 text-2xl font-bold text-center">
        Minting your certificate
      </h1>

      <p className="mt-3 text-base text-[#626262] text-center">
        Keep this page open
      </p>

      <div className="mt-6 flex flex-col gap-3 items-start">
        <BkfcStepRow state="done" label="Transaction submitted" />
        <BkfcStepRow
          state={status === "waiting-confirmation" ? "active" : "done"}
          label="Waiting for confirmation"
        />
        <BkfcStepRow
          state={status === "verifying" ? "active" : "pending"}
          label="Backend verifies on chain"
        />
      </div>
    </div>
  );

  // --------------------------------------------------------------- claimed
  const renderClaimed = () => {
    const metadataUrl = `${process.env.REACT_APP_BASE_URL || ""}/metadata/bkfc/${uid}`;
    const metadataLabel = metadataUrl.replace(/^https?:\/\//, "");

    return (
      <div className="px-6 pt-8 pb-6">
        <div className="flex justify-center">
          <img
            src="/images/bkfc/congrats-badge.png"
            alt="Claimed"
            className="w-[66px] h-[66px]"
          />
        </div>

        <h1 className="mt-3 text-2xl font-bold text-center">
          Certificate claimed
        </h1>

        <img
          src="/images/bkfc/cert-card.png"
          alt="BKFC Certificate of Authenticity"
          className="mt-6 w-full h-[264px] object-cover rounded-[22px]"
        />

        <p className="mt-3 text-base text-[#626262] text-center">
          Also sent to {email}
        </p>

        <button
          onClick={handleRunRaffle}
          disabled={raffleSending}
          className="mt-6 w-full h-[46px] rounded-[80px] bg-[#FFD43E] text-black font-bold text-base transition-colors hover:bg-[#f5c822] disabled:opacity-60"
        >
          {raffleSending ? "Sending link..." : "Claim my Reward"}
        </button>

        <button
          onClick={() => navigate("/rewards/bkfc")}
          className="mt-3 w-full h-[46px] rounded-[80px] border-2 border-black text-black font-bold text-base transition-colors hover:bg-gray-100"
        >
          My certificates
        </button>

        <div className="mt-6 space-y-1.5 text-sm">
          <div className="flex items-center justify-between">
            <span>COLLECTION</span>
            <span className="font-medium uppercase">223 X BKFC</span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span>CHIP UID</span>
            <span className="font-medium uppercase truncate">{uid}</span>
          </div>
          <div className="flex items-center justify-between">
            <span>TOKEN</span>
            <span className="font-medium uppercase">
              {shortAddress(result?.tokenAddress)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span>NETWORK</span>
            <span className="font-medium uppercase">{BKFC_NETWORK_LABEL}</span>
          </div>
          <div className="flex items-center justify-between">
            <span>WALLET</span>
            <span className="font-medium uppercase">
              {walletLabel} · {shortAddress(accountAddress)}
            </span>
          </div>
        </div>

        <p className="mt-6 text-base text-[#626262]">
          Metadata:
          <br />
          <a
            href={metadataUrl}
            target="_blank"
            rel="noreferrer"
            className="block truncate"
          >
            {metadataLabel}
          </a>
        </p>
      </div>
    );
  };

  // ----------------------------------------------------------- check email
  const renderCheckEmail = () => (
    <div className="px-6 pt-8 pb-6">
      <BkfcBackButton onClick={() => setView("claimed")} />

      <div className="min-h-[55vh] flex flex-col justify-center">
      <h1 className="text-2xl font-bold text-center">Check your email</h1>

      <p className="mt-6 text-base text-[#626262]">
        We sent a raffle link to <span className="underline">{email}</span>.
        Open it to enter the raffle associated with this certificate.
      </p>

      {raffleNotice && (
        <p className="mt-3 text-sm text-[#626262] text-center">{raffleNotice}</p>
      )}

      <div className="mt-6 w-full rounded-[10px] bg-[#FFF0F0] border border-[#B94C4C] p-2.5 text-center">
        <span className="text-sm text-[#B94C4C] uppercase">
          One reward per purchase
        </span>
      </div>

      {/* {raffleUrl && (
        <button
          onClick={() => {
            window.location.href = raffleUrl;
          }}
          className="mt-6 w-full h-[46px] rounded-[80px] bg-black text-white font-bold text-base transition-colors hover:bg-gray-800"
        >
          Open the link
        </button>
      )} */}

      <button
        onClick={() => navigate("/rewards/bkfc")}
        className={`${raffleUrl ? "mt-3" : "mt-6"} w-full h-[46px] rounded-[80px] border-2 border-black text-black font-bold text-base transition-colors hover:bg-gray-100`}
      >
        Didn&rsquo;t get it? Resend from My Certificates
      </button>
      </div>
    </div>
  );

  return (
    <>
      {view === "home" && renderHome()}
      {view === "confirm" && renderConfirm()}
      {view === "approve" && renderApprove()}
      {view === "minting" && renderMinting()}
      {view === "claimed" && renderClaimed()}
      {view === "check_email" && renderCheckEmail()}

      {/* Checking this tag (initial on-chain status read) */}
      {drawer === "checking" && (
        <BkfcDrawer>
          <div className="flex flex-col items-center gap-5">
            <img
              src="/images/bkfc/loading-badge.png"
              alt="Loading"
              className="w-20 animate-pulse"
            />
            <div className="text-center w-full">
              <div className="text-xl font-bold text-black">
                Checking this tag
              </div>
              <div className="mt-3 text-[#626262]">
                Reading claim status from Aptos
              </div>
              <div className="mt-3 text-[10px] text-[#626262] break-all">
                {uid}
              </div>
            </div>
          </div>
        </BkfcDrawer>
      )}

      {/* Connect to claim */}
      {drawer === "connect" && (
        <BkfcConnectDrawer
          onClose={() => setDrawer(null)}
          onConnected={handleConnected}
        />
      )}

      {/* Claim with your email */}
      {drawer === "email" && (
        <BkfcDrawer onClose={() => setDrawer(null)}>
          <div className="text-[23px] font-bold text-black">
            Claim with your email
          </div>

          <div className="mt-2.5 text-lg text-[#626262]">
            Please enter your email address to claim your reward.
          </div>

          <div className="mt-5 relative">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email address"
              className="w-full h-[46px] pl-[46px] pr-3.5 bg-[#F8F8F8] border border-[#B4B4B4] text-base font-bold text-black placeholder:font-normal placeholder:text-[#B4B4B4] focus:outline-none"
            />

            <img
              src="/svg/bkfc/email-icon.svg"
              alt=""
              className="absolute left-3.5 top-1/2 -translate-y-1/2 w-6 h-6"
            />
          </div>

          {emailError && (
            <div className="mt-2.5 px-4 py-3 text-sm text-red-700 bg-red-100 rounded border border-red-400">
              {emailError}
            </div>
          )}

          <button
            onClick={handleEmailSubmit}
            disabled={!email}
            className="mt-6 w-full h-10 font-bold text-white bg-[#1F1F1F] transition-colors hover:bg-gray-800 disabled:bg-[#B4B4B4] disabled:cursor-not-allowed"
          >
            {email ? "Claim" : "Connect"}
          </button>
        </BkfcDrawer>
      )}

      {/* Already claimed / invalid */}
      {drawer === "already_claimed" && (
        <BkfcDrawer>
          <div className="flex flex-col items-center gap-5">
            <img
              src="/images/bkfc/error-badge.png"
              alt="Error"
              className="w-[70px] h-[70px]"
            />

            <div className="text-center w-full">
              <div className="text-xl font-bold text-black">Error</div>
              <div className="mt-3 text-[#626262]">
                This collectible has already been claimed or is invalid.
              </div>
            </div>

            <button
              onClick={() => {
                setDrawer(null);
                reset();
              }}
              className="w-full h-10 font-bold text-white bg-[#1F1F1F] border-2 border-black transition-colors hover:bg-gray-800"
            >
              Please Try Again
            </button>

            <div className="flex items-center gap-2 px-2 w-full">
              <img
                src="/svg/bkfc/exclamation-circle.svg"
                alt=""
                className="w-4 h-4"
              />
              <span className="text-sm font-helv font-light">
                Please contact support if you believe this is an error.
              </span>
            </div>
          </div>
        </BkfcDrawer>
      )}

      {/* Generic error */}
      {drawer === "error" && (
        <BkfcDrawer>
          <div className="flex flex-col items-center gap-5">
            <img
              src="/images/bkfc/error-badge.png"
              alt="Error"
              className="w-[70px] h-[70px]"
            />

            <div className="text-center w-full">
              <div className="text-xl font-bold text-black">
                {error?.title ?? "Error"}
              </div>
              <div className="mt-3 text-[#626262]">
                {errorMessage ||
                  "This collectible has already been claimed or is invalid."}
              </div>
            </div>

            <button
              onClick={handleErrorRetry}
              className="w-full h-10 font-bold text-white bg-[#1F1F1F] border-2 border-black transition-colors hover:bg-gray-800"
            >
              Please Try Again
            </button>

            <div className="flex items-center gap-2 px-2 w-full">
              <img
                src="/svg/bkfc/exclamation-circle.svg"
                alt=""
                className="w-4 h-4"
              />
              <span className="text-sm font-helv font-light">
                Please contact support if you believe this is an error.
              </span>
            </div>
          </div>
        </BkfcDrawer>
      )}
    </>
  );
};
